/**
 * Projects API Client - HTTP client for project endpoints
 */

import type {
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateGitHubConfigRequest,
  UpdateGitHubConfigRequest,
  UpsertJiraConfigRequest,
  UpsertSlackConfigRequest,
  GitHubConfigResponse,
  JiraConfigResponse,
  SlackConfigResponse,
  ProjectStatus,
} from '@resolve-ai/shared';
import { ProjectStatuses } from '@resolve-ai/shared';

import { ApiError, fetchWithAuth } from './api-client';

// Project Management API

/**
 * Project list item (slim response for list view)
 */
export interface ProjectListItem {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  agentContext: string | null;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  githubRepoCount: number;
  jiraProjectKey: string | null;
}

/**
 * Project with full configurations (for detail view)
 */
export interface ProjectWithConfigs {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  agentContext: string | null;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  githubConfigs: Array<{
    id: string;
    projectId: string;
    repoOwner: string;
    repoName: string;
    repoFullName: string;
    defaultBranch: string;
    createdAt: string;
    updatedAt: string;
  }>;
  jiraConfig: {
    id: string;
    projectId: string;
    jiraProjectKey: string;
    jiraProjectName: string | null;
    jiraFilterJql: string | null;
    assignee: string | null;
    assigneeDisplayName: string | null;
    startStatus: string | null;
    endStatus: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
  slackConfig: {
    id: string;
    projectId: string;
    webhookUrl: string;
    notifyOnPrCreated: boolean;
    notifyOnFailure: boolean;
    createdAt: string;
    updatedAt: string;
  } | null;
}


/**
 * Project counts by status
 */
export interface ProjectCounts {
  active: number;
  inactive: number;
  archived: number;
}

/**
 * Projects API client
 */
export const projectsApi = {
  /**
   * List all projects (returns slim items for performance)
   */
  async list(status?: 'all' | 'active' | 'inactive' | 'archived'): Promise<ProjectListItem[]> {
    const queryParams = new URLSearchParams();
    if (status === 'all') {
      // 'all' means active + inactive (exclude archived)
      queryParams.append('status', `${ProjectStatuses.ACTIVE},${ProjectStatuses.INACTIVE}`);
    } else if (status) {
      queryParams.append('status', status);
    }

    const url = `/projects${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetchWithAuth<{ projects: ProjectListItem[] }>(url);
    return response.projects;
  },

  /**
   * Get project counts by status
   */
  async getCounts(): Promise<ProjectCounts> {
    const response = await fetchWithAuth<ProjectCounts>('/projects/counts');
    return response;
  },

  /**
   * Create a new project
   */
  async create(data: CreateProjectRequest): Promise<ProjectWithConfigs> {
    const response = await fetchWithAuth<{ project: ProjectWithConfigs }>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.project;
  },

  /**
   * Get project by ID
   */
  async get(id: string): Promise<ProjectWithConfigs> {
    const response = await fetchWithAuth<{ project: ProjectWithConfigs }>(`/projects/${id}`);
    return response.project;
  },

  /**
   * Update project
   */
  async update(id: string, data: UpdateProjectRequest): Promise<ProjectWithConfigs> {
    const response = await fetchWithAuth<{ project: ProjectWithConfigs }>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response.project;
  },

  /**
   * Delete project
   */
  async delete(id: string): Promise<void> {
    await fetchWithAuth<{ message: string }>(`/projects/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Queue a task for a Jira ticket on demand (admin only), regardless of
   * whether a task or PR already exists for it
   */
  async manualImportTask(projectId: string, ticketKey: string): Promise<{ message: string }> {
    return fetchWithAuth<{ message: string }>(`/projects/${projectId}/tasks/manual-import`, {
      method: 'POST',
      body: JSON.stringify({ ticketKey }),
    });
  },

  // GitHub Configuration API

  /**
   * List GitHub configs for a project
   */
  async listGitHubConfigs(projectId: string): Promise<GitHubConfigResponse[]> {
    const response = await fetchWithAuth<{ configs: GitHubConfigResponse[] }>(
      `/projects/${projectId}/github`
    );
    return response.configs;
  },

  /**
   * Add GitHub repository to project
   */
  async addGitHubConfig(
    projectId: string,
    data: CreateGitHubConfigRequest
  ): Promise<GitHubConfigResponse> {
    const response = await fetchWithAuth<GitHubConfigResponse>(`/projects/${projectId}/github`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  },

  /**
   * Update GitHub config
   */
  async updateGitHubConfig(
    projectId: string,
    configId: string,
    data: UpdateGitHubConfigRequest
  ): Promise<GitHubConfigResponse> {
    const response = await fetchWithAuth<GitHubConfigResponse>(
      `/projects/${projectId}/github/${configId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response;
  },

  /**
   * Remove GitHub repository from project
   */
  async deleteGitHubConfig(projectId: string, configId: string): Promise<void> {
    await fetchWithAuth<{ message: string }>(`/projects/${projectId}/github/${configId}`, {
      method: 'DELETE',
    });
  },

  // Jira Configuration API

  /**
   * Get Jira config for a project
   */
  async getJiraConfig(projectId: string): Promise<JiraConfigResponse | null> {
    try {
      return await fetchWithAuth<JiraConfigResponse>(`/projects/${projectId}/jira`);
    } catch (err) {
      // 404 means no config exists
      if (err instanceof ApiError && err.statusCode === 404) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Create or update Jira config for a project
   */
  async upsertJiraConfig(
    projectId: string,
    data: UpsertJiraConfigRequest
  ): Promise<JiraConfigResponse> {
    const response = await fetchWithAuth<JiraConfigResponse>(`/projects/${projectId}/jira`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  },

  /**
   * Remove Jira config from project
   */
  async deleteJiraConfig(projectId: string): Promise<void> {
    await fetchWithAuth<{ message: string }>(`/projects/${projectId}/jira`, {
      method: 'DELETE',
    });
  },

  // Slack Configuration API

  /**
   * Get Slack config for a project
   */
  async getSlackConfig(projectId: string): Promise<SlackConfigResponse | null> {
    try {
      return await fetchWithAuth<SlackConfigResponse>(`/projects/${projectId}/slack`);
    } catch (err) {
      // 404 means no config exists
      if (err instanceof ApiError && err.statusCode === 404) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Create or update Slack config for a project
   */
  async upsertSlackConfig(
    projectId: string,
    data: UpsertSlackConfigRequest
  ): Promise<SlackConfigResponse> {
    const response = await fetchWithAuth<SlackConfigResponse>(`/projects/${projectId}/slack`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  },

  /**
   * Remove Slack config from project
   */
  async deleteSlackConfig(projectId: string): Promise<void> {
    await fetchWithAuth<{ message: string }>(`/projects/${projectId}/slack`, {
      method: 'DELETE',
    });
  },
};
